import { DEFAULT_SORT_OPTION, FILTER_VALUE_MAP } from 'constants/providerReview';

import { REVIEW_LIST_LOAD_MORE_LIMIT } from 'constants/common';

import type { URLSearchQuery } from 'core/types/ReviewList';

import type { Companions, Gender, Purposes, SortBy } from 'store/reviewList/types';

function reverseMap(map: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));
}

const REVERSE_MONTH_MAP = reverseMap(FILTER_VALUE_MAP.stayedMonths);
const REVERSE_SCORE_MAP = reverseMap(FILTER_VALUE_MAP.overallScores);
const REVERSE_OBJ_MAP = reverseMap(FILTER_VALUE_MAP.purposes);
const REVERSE_AGE_MAP = reverseMap(FILTER_VALUE_MAP.ageRanges);
const REVERSE_SORT_MAP = reverseMap(FILTER_VALUE_MAP.sortBy);
const REVERSE_COMPANIONS_MAP = reverseMap(FILTER_VALUE_MAP.companions);
const REVERSE_GENDER_MAP = reverseMap(FILTER_VALUE_MAP.genders);
const REVERSE_ADDITIONAL_INFO_MAP = reverseMap(FILTER_VALUE_MAP.additionalInformation);

export function getDefaultURLSearchRequest(query?: URLSearchQuery) {
  const topics = new Set<string>();
  const stayedMonths = new Set<string>();
  const overallScores = new Set<string>();
  const purposes = new Set<string>();
  const companions = new Set<string>();
  const ageRanges = new Set<string>();
  const additionalInformation = new Set<string>();
  const genders = new Set<string>();
  const pageNumber = query?.page || 1;

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      const valueArray = Array.isArray(value) ? value : [value];

      valueArray.forEach((paramValue) => {
        // topics
        const match_topic = key.match(/^f_topic_(.+)$/);

        if (match_topic && paramValue === '1') {
          topics.add(match_topic[1].replace(/_/g, ' '));
        }

        // stayedMonths
        const match_time = key.match(/^f_time_(\d{2})$/);

        if (match_time && paramValue === '1' && REVERSE_MONTH_MAP[match_time[1]]) {
          stayedMonths.add(REVERSE_MONTH_MAP[match_time[1]]);
        }

        // overallScores
        const match_score = key.match(/^f_score_(\d)$/);

        if (match_score && paramValue === '1' && REVERSE_SCORE_MAP[match_score[1]]) {
          overallScores.add(REVERSE_SCORE_MAP[match_score[1]]);
        }

        // purposes
        const match_obj = key.match(/^f_obj_([a-z])$/);

        if (match_obj && paramValue === '1' && REVERSE_OBJ_MAP[match_obj[1]]) {
          purposes.add(REVERSE_OBJ_MAP[match_obj[1]]);
        }

        // companions
        const match_mem = key.match(/^f_mem(\d)$/);

        if (match_mem && paramValue === '1' && REVERSE_COMPANIONS_MAP[match_mem[1]]) {
          companions.add(REVERSE_COMPANIONS_MAP[match_mem[1]]);
        }

        // ageRanges
        const match_age = key.match(/^f_age_(\d{2})$/);

        if (match_age && paramValue === '1' && REVERSE_AGE_MAP[match_age[1]]) {
          ageRanges.add(REVERSE_AGE_MAP[match_age[1]]);
        }

        // genders
        const match_sex = key.match(/^f_sex_([mf])$/);

        if (match_sex && paramValue === '1' && REVERSE_GENDER_MAP[match_sex[1]]) {
          genders.add(REVERSE_GENDER_MAP[match_sex[1]]);
        }

        // additionalInformation
        const match_info = key.match(/^f_info_([a-z]+)$/);

        if (match_info && paramValue === '1' && REVERSE_ADDITIONAL_INFO_MAP[match_info[1]]) {
          additionalInformation.add(REVERSE_ADDITIONAL_INFO_MAP[match_info[1]]);
        }
      });
    });
  }

  return {
    sortBy:
      query?.f_sort && REVERSE_SORT_MAP[query.f_sort]
        ? (REVERSE_SORT_MAP[query.f_sort] as SortBy)
        : (DEFAULT_SORT_OPTION as SortBy),
    offset: (pageNumber - 1) * REVIEW_LIST_LOAD_MORE_LIMIT,
    limit: REVIEW_LIST_LOAD_MORE_LIMIT,
    keywords: query?.f_keyword ? query.f_keyword.split(/\s+/).filter(Boolean) : [],
    topics: Array.from(topics),
    stayedMonths: Array.from(stayedMonths),
    overallScores: Array.from(overallScores),
    purposes: Array.from(purposes).map((p) => p as Purposes),
    companions: Array.from(companions).map((c) => c as Companions),
    additionalInformation: Array.from(additionalInformation),
    ageRanges: Array.from(ageRanges),
    genders: Array.from(genders).map((g) => g as Gender),
  };
}
